import type {CombatantEvent, CombatantSnapshot} from "@/api/CombatantApi.ts";
import type Combatant from "@/encounter/model/Combatant.ts";
import type {CombatantData} from "@/encounter/model/CombatantData.ts";
import type {Character} from "@/characters/model/Character.ts";

export type CombatantAction =
    | {type: "reset"}
    | {type: "replace"; snapshot: CombatantSnapshot}
    | {type: "event"; event: CombatantEvent};

type TransformInteractionState = {
    sequence: number;
    final: boolean;
};

export type CombatantState = {
    combatantsById: ReadonlyMap<string, CombatantData>;
    charactersById: ReadonlyMap<string, Character>;
    transformInteractions: ReadonlyMap<string, TransformInteractionState>;
};

export function combatantReducer(state: CombatantState, action: CombatantAction): CombatantState {
    if (action.type === "reset") return emptyCombatantState();
    if (action.type === "replace") {
        return {
            combatantsById: new Map(action.snapshot.combatants.map(combatant => [combatant.id, combatant])),
            charactersById: addCharacters(state.charactersById, action.snapshot.characters),
            transformInteractions: new Map(),
        };
    }

    const upserted = action.event.upserted ?? [];
    const removedIds = action.event.removedIds ?? [];
    const characters = action.event.characters ?? [];
    const transformed = action.event.transformed;
    const currentInteraction = transformed
        ? state.transformInteractions.get(transformed.interactionId)
        : undefined;
    const acceptTransform = transformed !== undefined && (
        currentInteraction === undefined ||
        !currentInteraction.final && (
            transformed.sequence > currentInteraction.sequence ||
            transformed.sequence === currentInteraction.sequence && transformed.final
        )
    );
    if (upserted.length === 0 && removedIds.length === 0 && characters.length === 0 && !acceptTransform) return state;

    const updated = new Map(state.combatantsById);
    upserted.forEach(combatant => updated.set(combatant.id, combatant));
    removedIds.forEach(id => updated.delete(id));
    if (acceptTransform && transformed) {
        transformed.transforms.forEach(transform => {
            const combatant = updated.get(transform.id);
            if (combatant) updated.set(transform.id, {
                ...combatant,
                position: transform.position,
                rotation: transform.rotation,
                levelId: transform.levelId ?? combatant.levelId,
            });
        });
    }

    const transformInteractions = new Map(state.transformInteractions);
    if (acceptTransform && transformed) {
        transformInteractions.set(transformed.interactionId, {
            sequence: transformed.sequence,
            final: transformed.final,
        });
        while (transformInteractions.size > 200) {
            const oldestInteraction = transformInteractions.keys().next().value;
            if (oldestInteraction === undefined) break;
            transformInteractions.delete(oldestInteraction);
        }
    }

    return {combatantsById: updated, charactersById: addCharacters(state.charactersById, characters), transformInteractions};
}

export function emptyCombatantState(): CombatantState {
    return {combatantsById: new Map(), charactersById: new Map(), transformInteractions: new Map()};
}

function addCharacters(existing: ReadonlyMap<string, Character>, incoming: Character[]): ReadonlyMap<string, Character> {
    const additions = incoming.filter(character => !existing.has(character.id));
    if (additions.length === 0) return existing;
    const characters = new Map(existing);
    additions.forEach(character => characters.set(character.id, character));
    return characters;
}

export function createCombatantSelector() {
    const cache = new WeakMap<CombatantData, Combatant>();
    return (state: CombatantState): Combatant[] => Array.from(state.combatantsById.values()).flatMap(data => {
        const {characterId, ...token} = data;
        const character = state.charactersById.get(characterId);
        // A token update can arrive before the event introducing its template.
        // Retain the data and display the token as soon as the template arrives.
        if (!character) return [];
        const previous = cache.get(data);
        if (previous?.character === character) return [previous];
        const displayed: Combatant = {...token, character};
        cache.set(data, displayed);
        return [displayed];
    });
}
